import { Trans, useLingui } from '@lingui/react/macro';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ShiftSet } from '@/types/template';

import { VisualPreview } from './visual-preview';

/**
 * Wizard preview modal (M2). Wraps `VisualPreview` in a wide dialog
 * so the user can see the in-progress template's recurring weekly
 * schedule from Step 2 without leaving the shifts list. Mirrors the
 * landing-page `RotaPreviewModal` (TM3) shell — same width, same
 * close affordances — so the two paths to "see the schedule" feel
 * the same.
 */
export function PreviewModal({ shifts, onClose }: PreviewModalProps) {
  const { t } = useLingui();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[90vh] w-full max-w-[95vw] flex-col gap-0 p-0 sm:max-w-[95vw]"
      >
        <DialogHeader className="flex-shrink-0 border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>
                <Trans>Preview</Trans>
              </DialogTitle>
              <DialogDescription>
                <Trans>Recurring weekly schedule preview.</Trans>
              </DialogDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label={t`Dismiss`}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {shifts.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              <Trans>This template has no shifts yet.</Trans>
            </p>
          ) : (
            <VisualPreview shifts={shifts} />
          )}
        </div>

        <div className="mt-auto flex flex-shrink-0 justify-end border-t px-6 py-4">
          <Button onClick={onClose}>
            <Trans>Close preview</Trans>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface PreviewModalProps {
  shifts: ShiftSet[];
  onClose: () => void;
}
